import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * timeAgo — coarse "3h ago" / "2d ago" formatter. Kept intentionally tiny;
 * we don't need moment/date-fns for a single chip.
 */
const timeAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return "long ago";
};

/**
 * TrainingHealthChip — sidebar chip that shows operators whether Mimi is
 * actually firing their trained Q&A pairs on real calls. Three states:
 *
 *   • 0 pairs   → gentle CTA "Train Mimi with your first Q&A →"
 *   • pairs, no matches yet → "N trained pairs live · added Xd ago"
 *   • pairs + at least one match → "N trained pairs live · matched Xh ago"
 *
 * Powered by GET /api/mimi/training/stats. Zero cost — the [TRAINED] system
 * tag Mimi is instructed to prefix onto her replies is what updates the
 * "matched Xh ago" timestamp on the business record.
 */
export default function TrainingHealthChip() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/mimi/training/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return null;

  const count = stats.active_count || 0;
  const matched = timeAgo(stats.last_matched_at);
  const added = timeAgo(stats.last_added_at);

  if (count === 0) {
    return (
      <Link
        to="/dashboard/mimi"
        className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-xs bg-[#FEF3C7] text-[#92400E] hover:bg-[#FCE7A1] transition-colors"
        data-testid="training-health-chip-empty"
      >
        <Brain size={14} weight="fill" />
        <span className="flex-1">Train Mimi with your first Q&amp;A →</span>
      </Link>
    );
  }

  const tone = matched
    ? "bg-[#C2D1C1]/60 text-[#1E3A2F] hover:bg-[#C2D1C1]"
    : "bg-[#E6E2D6] text-[#1E3A2F] hover:bg-[#DAD5C4]";

  const suffix = matched
    ? `matched ${matched}`
    : added
    ? `added ${added}`
    : "no matches yet";

  const tip = matched
    ? `Mimi last used one of your trained answers ${matched}. Click to see all pairs.`
    : `You have ${count} trained ${count === 1 ? "pair" : "pairs"} live. Waiting for the first caller to trigger one. Click to review.`;

  return (
    <Link
      to="/dashboard/mimi"
      className={`flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-xs transition-colors ${tone}`}
      data-testid="training-health-chip"
      title={tip}
    >
      <CheckCircle size={14} weight="fill" />
      <span className="flex-1">
        <b>{count}</b> trained {count === 1 ? "pair" : "pairs"} live
        <span className="opacity-70"> · {suffix}</span>
      </span>
    </Link>
  );
}
